import { useState } from 'react'
import { ArrowRight, Loader2 } from 'lucide-react'
import { PageMeta } from '@/components/seo/PageMeta'
import { LandingLayout } from '@/components/layout/LandingLayout'
import { contactService, type ContactFormRequest } from '@/lib/api/services/contact'
import { FormField, FormPage } from '@/components/landing/EditorialForm'

const contactReasons = [
  { value: 'sales', label: 'Book a demo' },
  { value: 'support', label: 'Get technical support' },
  { value: 'partnership', label: 'Discuss a partnership' },
  { value: 'press', label: 'Talk to press & media' },
  { value: 'other', label: 'Ask something else' },
]

const emptyForm: ContactFormRequest = {
  name: '',
  email: '',
  phone: '',
  company: '',
  reason: '',
  message: '',
}

export default function Contact() {
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [formData, setFormData] = useState<ContactFormRequest>(emptyForm)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    setError(null)

    try {
      await contactService.submit(formData)
      setSubmitted(true)
      setFormData(emptyForm)
    } catch (err) {
      // Keep what the visitor typed so they can try again.
      setError(`Your message was not sent. ${(err as Error).message}`)
    } finally {
      setSubmitting(false)
    }
  }

  const set = (field: keyof typeof formData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => setFormData({ ...formData, [field]: e.target.value })

  return (
    <>
      <PageMeta
        title="Contact Us | Templatean"
        description="Get in touch with the Templatean team. We're here to help with demos, technical support, partnerships, and more."
      />

      <LandingLayout>
        <FormPage
          title={['You bring the templates.', 'We bring the agents.']}
          lede={
            <p>
              Tell us about your documents, your brand and your team. Whether you have a clear
              brief or just a first question, we will get back to you within 24 hours with a
              straight answer on how Templatean can help.
            </p>
          }
        >
          {submitted ? (
            <div className="max-w-xl">
              <p className="landing-text-display-lg text-[var(--color-landing-gray-900)] dark:text-white">
                Thanks. We will be in touch.
              </p>
              <p className="mt-4 text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]">
                Your message is with the team. Expect a reply within 24 hours.
              </p>
              <button
                type="button"
                onClick={() => setSubmitted(false)}
                className="landing-cta landing-cta--outline landing-cta--md landing-btn-sweep mt-10"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="grid gap-3 sm:grid-cols-2">
              <FormField id="name" label="Your name">
                <input id="name" className="landing-field" placeholder="Your name" autoComplete="name" required value={formData.name} onChange={set('name')} />
              </FormField>
              <FormField id="company" label="Company">
                <input id="company" className="landing-field" placeholder="Company" autoComplete="organization" value={formData.company} onChange={set('company')} />
              </FormField>
              <FormField id="phone" label="Your phone number">
                <input id="phone" type="tel" className="landing-field" placeholder="Your phone number" autoComplete="tel" value={formData.phone} onChange={set('phone')} />
              </FormField>
              <FormField id="email" label="Your work email">
                <input id="email" type="email" className="landing-field" placeholder="Your work email" autoComplete="email" required value={formData.email} onChange={set('email')} />
              </FormField>
              <FormField id="reason" label="I want to..." className="sm:col-span-2">
                <select id="reason" className="landing-field" required value={formData.reason} onChange={set('reason')}>
                  <option value="" disabled>
                    I want to...
                  </option>
                  {contactReasons.map((reason) => (
                    <option key={reason.value} value={reason.value}>
                      {reason.label}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField id="message" label="Your message" className="sm:col-span-2">
                <textarea id="message" className="landing-field" placeholder="Your message..." rows={5} required value={formData.message} onChange={set('message')} />
              </FormField>
              {error && (
                <p role="alert" className="sm:col-span-2 mt-4 border-l-2 border-[#ff00f2] pl-4 text-[var(--color-landing-gray-900)] dark:text-white">
                  {error}
                </p>
              )}
              <div className="sm:col-span-2 mt-7">
                <button
                  type="submit"
                  disabled={submitting}
                  className="landing-cta landing-cta--solid landing-cta--lg landing-btn-sweep disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Sending
                    </>
                  ) : (
                    <>
                      Send form
                      <ArrowRight className="h-5 w-5" />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </FormPage>
      </LandingLayout>
    </>
  )
}
